import type {
    LineTax,
    OrderLine,
    OrderLineInput,
    OrderLineLists,
    OrderLineProduct,
    OrderLineStatus,
} from '@/features/orders/api';
import { COPY } from '@/features/orders/copy';

const E = COPY.lines.errors;

/*
  One order line as the line editor holds it before it is saved (ALS-63). The editor is shared:
  New order mounts it in its Add product drawers and Edit product, and the order page's drawers
  will mount the same for a line still Pending (ALS-259), so nothing here knows which screen it
  is on. It knows the product the line is of and the registers it chooses from, and nothing else.

  What a line stores is dbo.OrderDetail, and the arithmetic is the legacy's, read off the live
  lines rather than off the prototype:

    gross        = qty x sale price                        (OrderDetail.Rate is the unit price)
    tax added    : TaxAmount = gross x rate,              AmountTotal = gross + TaxAmount
    tax included : TaxAmount = gross - gross / (1 + rate), AmountTotal = gross

  The discount is the supplier's trade discount, not the client's: on every discounted legacy line
  the cost price is the product's price less the discount and the rate is the full price. So a
  discount works out the cost, and leaves what the client pays alone. It is off while commission
  applies, because then the margin is the supplier's commission instead.

  The margin a line earns is not worked out here: that arithmetic is the money epic's (ALS-26).
*/

/** Every line starts here, whatever the design's label for the moment calls it. */
export const NEW_LINE_STATUS: OrderLineStatus = 'Pending';

export interface LineDraft {
    /** The draft's own handle, stable across edits and unique on the order. */
    key: string;
    product: OrderLineProduct;
    status: OrderLineStatus;
    /** Typed, so a field mid-edit is not rewritten under the caret. */
    quantity: string;
    sizeId: number | null;
    colourId: number | null;
    /** A row of the tax register, copied onto the line with whether the price includes it. */
    taxId: number;
    taxIncluded: boolean;
    countryId: number | null;
    dropShip: boolean;
    preOrder: boolean;
    /** An ISO day or empty: when the client needs the piece. */
    neededBy: string;
    /** An ISO day or empty: when a pre-ordered piece becomes available (EstimatedDeliveryDate). */
    availableFrom: string;
    salePrice: string;
    costPrice: string;
    supplierId: number | null;
    /** Whether the supplier's commission applies; the rate is the supplier's, as of the order date. */
    commission: boolean;
    /** A whole percentage, or empty for none. */
    discount: string;
    /** The A-List fee on this line, money; OrderDetail.AdminCharges. */
    aListFee: string;
    /** The Xero sales account the line posts to, copied from the product and changeable on the line only (ALS-386). */
    accountId: number | null;
}

export type LineDraftField = Exclude<keyof LineDraft, 'key' | 'product' | 'status'>;
export type LineDraftErrors = Partial<Record<LineDraftField, string>>;

let sequence = 0;
/** A key no other draft on the page carries. */
export function newLineKey(): string {
    sequence += 1;
    return `line-${Date.now().toString(36)}-${sequence}`;
}

/** Money as the inputs hold it: two decimals, no symbol. */
export function moneyText(value: number): string {
    return value.toFixed(2);
}

/** The rate the line's supplier would earn on this product, or null where it has none. */
export function commissionRate(
    product: OrderLineProduct,
    supplierId: number | null,
): number | null {
    if (supplierId === null) return null;
    return product.commissions.find((c) => c.supplierId === supplierId)?.rate ?? null;
}

/**
 * A new line, filled from the product: its price, cost, tax row, country and supplier, and its
 * only colour and size where it has just one of each. The supplier is the product's own only when
 * that supplier may still take new orders; commission is on when the supplier earns some.
 */
export function lineFromProduct(product: OrderLineProduct, lists: OrderLineLists): LineDraft {
    const supplierId = lists.suppliers.some((s) => s.id === product.supplierId)
        ? product.supplierId
        : null;
    const taxLive = lists.taxes.some((t) => t.id === product.taxId);
    const accountOffered = lists.accounts.some((a) => a.id === product.accountId);
    return {
        key: newLineKey(),
        product,
        status: NEW_LINE_STATUS,
        quantity: '1',
        sizeId: product.sizes.length === 1 ? product.sizes[0].id : null,
        colourId: product.colours.length === 1 ? product.colours[0].id : null,
        // A product on a retired row starts with none, so the line cannot carry a row the
        // register no longer offers; the editor asks for one.
        taxId: taxLive ? product.taxId : 0,
        taxIncluded: product.taxIncluded,
        countryId: product.countryId,
        dropShip: false,
        preOrder: false,
        neededBy: '',
        availableFrom: '',
        salePrice: moneyText(product.price),
        costPrice: moneyText(product.costPrice),
        supplierId,
        commission: commissionRate(product, supplierId) !== null,
        discount: '',
        aListFee: '',
        // An account the register no longer offers is not one a new line may take; the editor asks.
        accountId: accountOffered ? product.accountId : null,
    };
}

/**
 * A saved line as the editor holds it, for a line still Pending on the order page (ALS-259): what
 * it was set with, read back from the order, over the product as it stands now.
 */
export function lineFromOrderLine(line: OrderLine, product: OrderLineProduct): LineDraft {
    return {
        key: `order-line-${line.id}`,
        product,
        status: line.status,
        quantity: String(line.qty),
        sizeId: line.edit.sizeId,
        colourId: line.edit.colourId,
        taxId: line.edit.taxId,
        taxIncluded: line.taxIncluded,
        countryId: line.edit.countryId,
        dropShip: line.dropShip,
        preOrder: line.preOrder,
        neededBy: line.neededBy ?? '',
        availableFrom: line.edit.availableFrom ?? '',
        salePrice: moneyText(line.unitPrice),
        costPrice: moneyText(line.costPrice),
        supplierId: line.edit.supplierId,
        commission: line.commission,
        discount: line.discountPercentage ? String(line.discountPercentage) : '',
        aListFee: line.aListFee ? moneyText(line.aListFee) : '',
        accountId: line.accountId,
    };
}

/** Whether two drafts would save the same line. */
export function sameLineRequest(a: LineDraft, b: LineDraft): boolean {
    return JSON.stringify(lineRequest(a)) === JSON.stringify(lineRequest(b));
}

/** A copy of a line as a line of its own. */
export function duplicateLine(line: LineDraft): LineDraft {
    return { ...line, key: newLineKey() };
}

/** A number typed into a field, or null when it is not one. */
export function parseNumber(text: string): number | null {
    const trimmed = text.trim();
    if (trimmed === '' || !/^-?\d*(\.\d+)?$/.test(trimmed)) return null;
    const value = Number(trimmed);
    return Number.isFinite(value) ? value : null;
}

const round2 = (value: number) => Math.round(value * 100) / 100;

/** The cost a trade discount leaves: the sale price less the discount. */
export function costAfterDiscount(salePrice: string, discount: string): string | null {
    const price = parseNumber(salePrice);
    const percent = parseNumber(discount);
    if (price === null || percent === null || percent <= 0) return null;
    return moneyText(round2(price * (1 - percent / 100)));
}

/**
 * What one change does to the rest of the line. The editor hands every edit through here, so
 * the line's rules live in one place rather than in the controls:
 *  - a new supplier brings its own commission, on where it earns one;
 *  - commission and a trade discount do not go together, so turning commission on clears it;
 *  - a discount, or a new sale price under one, works the cost price out;
 *  - pre-order off drops the availability date that only a pre-order carries.
 */
export function applyLineChange(line: LineDraft, change: Partial<LineDraft>): LineDraft {
    const next = { ...line, ...change };
    if ('supplierId' in change && change.supplierId !== line.supplierId) {
        next.commission = commissionRate(next.product, next.supplierId) !== null;
    }
    if (next.commission) next.discount = '';
    if ('discount' in change || ('salePrice' in change && next.discount !== '')) {
        const cost = costAfterDiscount(next.salePrice, next.discount);
        if (cost !== null) next.costPrice = cost;
    }
    if ('preOrder' in change && !next.preOrder) next.availableFrom = '';
    return next;
}

export interface LineTotals {
    quantity: number;
    /** qty x sale price. */
    gross: number;
    taxAmount: number;
    totalBeforeTax: number;
    /** OrderDetail.AmountTotal: what the line adds to the order, tax in. */
    total: number;
}

/** The line's figures, worked out as the legacy stores them. Nothing typed yet counts as nothing. */
export function lineTotals(line: LineDraft, taxes: readonly LineTax[]): LineTotals {
    const quantity = Math.max(0, Math.trunc(parseNumber(line.quantity) ?? 0));
    const price = parseNumber(line.salePrice) ?? 0;
    const gross = round2(quantity * price);
    const rate = (taxes.find((t) => t.id === line.taxId)?.percentage ?? 0) / 100;
    const taxAmount = round2(line.taxIncluded ? gross - gross / (1 + rate) : gross * rate);
    const total = line.taxIncluded ? gross : round2(gross + taxAmount);
    return { quantity, gross, taxAmount, totalBeforeTax: round2(total - taxAmount), total };
}

/**
 * What the line refuses, said at the field. Size and colour are not required: 338 of the 5,736
 * live lines since 2025 carry neither, the product having none to choose.
 */
export function validateLine(
    line: LineDraft,
    lists: Pick<OrderLineLists, 'taxes' | 'accounts'>,
): LineDraftErrors {
    const errors: LineDraftErrors = {};
    const taxes = lists.taxes;
    const quantity = parseNumber(line.quantity);
    if (quantity === null || !Number.isInteger(quantity) || quantity < 1)
        errors.quantity = E.quantity;
    if (!taxes.some((t) => t.id === line.taxId)) errors.taxId = E.tax;
    const sale = parseNumber(line.salePrice);
    if (sale === null || sale < 0) errors.salePrice = E.money;
    const cost = parseNumber(line.costPrice);
    if (line.costPrice.trim() === '') errors.costPrice = E.costPrice;
    else if (cost === null || cost < 0) errors.costPrice = E.money;
    if (line.supplierId === null) errors.supplierId = E.supplier;
    // Asked for only where there is one to pick: with none on offer the server posts the line to
    // Goods Sold, and New order must not be blocked for it.
    if (lists.accounts.length > 0 && !lists.accounts.some((a) => a.id === line.accountId))
        errors.accountId = E.account;
    if (line.discount.trim() !== '') {
        const discount = parseNumber(line.discount);
        if (discount === null || !Number.isInteger(discount) || discount < 0 || discount > 100)
            errors.discount = E.discount;
    }
    if (line.aListFee.trim() !== '') {
        const fee = parseNumber(line.aListFee);
        if (fee === null || fee < 0) errors.aListFee = E.money;
    }
    if (line.preOrder && line.availableFrom === '') errors.availableFrom = E.availableFrom;
    if (
        line.preOrder &&
        line.availableFrom !== '' &&
        line.neededBy !== '' &&
        line.neededBy < line.availableFrom
    )
        errors.neededBy = E.neededByBeforeAvailable;
    return errors;
}

/**
 * The line as the server takes it: Create order's lines, Add product on the order page, and a
 * Pending line repriced there (ALS-259). Called once the line has passed validateLine, so its
 * supplier is there; nothing typed counts as nothing.
 */
export function lineRequest(line: LineDraft): OrderLineInput {
    const amount = (text: string) => Math.max(0, parseNumber(text) ?? 0);
    const day = (text: string) => (text === '' ? null : text);
    return {
        productId: line.product.id,
        quantity: Math.max(0, Math.trunc(parseNumber(line.quantity) ?? 0)),
        sizeId: line.sizeId,
        colourId: line.colourId,
        taxId: line.taxId,
        taxIncluded: line.taxIncluded,
        countryId: line.countryId,
        dropShip: line.dropShip,
        preOrder: line.preOrder,
        neededBy: day(line.neededBy),
        availableFrom: line.preOrder ? day(line.availableFrom) : null,
        salePrice: amount(line.salePrice),
        costPrice: amount(line.costPrice),
        supplierId: line.supplierId ?? 0,
        commission: line.commission,
        discount: line.discount.trim() === '' ? null : Math.trunc(parseNumber(line.discount) ?? 0),
        aListFee: amount(line.aListFee),
        accountId: line.accountId,
    };
}

/** How many pieces the order carries: the order quantity the details block shows. */
export function orderQuantity(lines: readonly LineDraft[]): number {
    return lines.reduce(
        (sum, line) => sum + Math.max(0, Math.trunc(parseNumber(line.quantity) ?? 0)),
        0,
    );
}
